import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Button from '../../../shared/components/ui/Button';
import Loader from '../../../shared/components/ui/Loader';
import {
  getUserTrash,
  purgeUser,
  reinstateUser
} from '../../services/api/implementation/impl';
import {
  ARCHIVE_INVALIDATION_KEYS,
  canPurgeUsers
} from '../../utils/userArchiveAccess';

/**
 * Archived users.
 *
 * Nothing else in the product lists them — that is deliberate, so they stop
 * appearing in pickers and reports. Which is exactly why this view has to
 * exist: without it an archived user is unreachable, and "recoverable for 30
 * days" would be a promise nobody can act on.
 */
const ArchivedUsersModal = ({ currentUser, onClose }) => {
  const [busyId, setBusyId] = useState(null);
  const queryClient = useQueryClient();
  const mayPurge = canPurgeUsers(currentUser);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['userTrash'],
    queryFn: () => getUserTrash(100)
  });

  const refresh = () =>
    ARCHIVE_INVALIDATION_KEYS.forEach((key) =>
      queryClient.invalidateQueries({ queryKey: key })
    );

  const { mutate: restore } = useMutation({
    mutationFn: ({ id, reason }) => reinstateUser(id, reason),
    onMutate: ({ id }) => setBusyId(id),
    onSettled: () => setBusyId(null),
    onSuccess: (_data, { name }) => {
      toast.success(
        `${name} restored. They have a fresh invitation; their previous work stays with whoever took it over.`
      );
      refresh();
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not restore this user')
  });

  const { mutate: removeForGood } = useMutation({
    mutationFn: ({ id, reason }) => purgeUser(id, reason),
    onMutate: ({ id }) => setBusyId(id),
    onSettled: () => setBusyId(null),
    onSuccess: (_data, { name }) => {
      toast.success(`${name}'s details erased. Their past work keeps its history.`);
      refresh();
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not remove this user')
  });

  const handleRestore = (entry) => {
    const reason = window.prompt(`Why is ${entry.name} being restored?`);
    if (!reason?.trim()) return;
    restore({ id: entry.id, name: entry.name, reason: reason.trim() });
  };

  const handlePurge = (entry) => {
    // Irreversible, so make them type the name rather than click twice.
    const typed = window.prompt(
      `This erases ${entry.name}'s personal details and cannot be undone.\n\nType their name to confirm:`
    );
    if (typed?.trim() !== entry.name) {
      if (typed !== null) toast.error('Name did not match — nothing was removed');
      return;
    }
    removeForGood({
      id: entry.id,
      name: entry.name,
      reason: 'Removed permanently from archived users'
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <Loader isVisible={Boolean(busyId)} />

        <h2 className="text-lg font-semibold text-gray-900">Archived users</h2>
        <p className="mt-1 text-sm text-gray-600">
          These people can no longer sign in. Their records and past work are kept.
          Their personal details are erased automatically when the countdown runs
          out.
        </p>

        {isLoading && <p className="mt-6 text-sm text-gray-500">Loading…</p>}

        {!isLoading && entries.length === 0 && (
          <p className="mt-6 text-sm text-gray-500">Nobody is archived.</p>
        )}

        {entries.length > 0 && (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Archived</th>
                  <th className="py-2 pr-4">Erased in</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4 text-gray-900">
                      {e.name}
                      {e.reason && (
                        <span className="block text-xs text-gray-500">{e.reason}</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-gray-600">
                      {e.original_email || e.original_phone_number || '—'}
                    </td>
                    <td className="py-3 pr-4 text-gray-600">
                      {e.archived_at ? new Date(e.archived_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-3 pr-4 text-gray-600">
                      {e.days_until_purge === null
                        ? 'Kept indefinitely'
                        : e.days_until_purge === 0
                          ? 'Any time now'
                          : `${e.days_until_purge} day${e.days_until_purge === 1 ? '' : 's'}`}
                    </td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          displayType="secondary"
                          disabled={busyId === e.id}
                          onClick={() => handleRestore(e)}>
                          Restore
                        </Button>
                        {mayPurge && (
                          <Button
                            type="button"
                            disabled={busyId === e.id}
                            onClick={() => handlePurge(e)}>
                            Remove now
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Button type="button" displayType="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ArchivedUsersModal;
